<template>
    <div :inert="showModal" aria-hidden="showModal">
        <div class="mb-6">
            <h1 class="text-2xl font-bold mb-2">Veresiye Borçları</h1>
            <p class="text-gray-600 dark:text-gray-400">Sürücü veresiye borçlarını görüntüleyin ve yönetin</p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div class="bg-white  dark:bg-gray-800 p-4 rounded-lg shadow border border-light-border dark:border-dark-border">
                <h3 class="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Kalan Borç</h3>
                <p class="text-2xl font-bold text-red-600">{{ formatPrice(Number(totalDebt) || 0) }} ₺</p>
            </div>
        </div>

        <div class="flex flex-col md:flex-row justify-between items-center mb-4 gap-4">

            <h3 class="text-lg font-semibold">Tüm Sürücü Borçları</h3>

            <div class="flex gap-2 items-end">
                <div>
                    <input type="text" v-model="veresiyeSearchText" @input="filterVeresiyeLoans"
                        placeholder="Sürücü adı, TC no veya ehliyet no ile ara..."
                        class="h-10 px-3 rounded-lg border border-light-border dark:border-gray-600 bg-primary-bg dark:bg-gray-800 text-sm text-primary-text dark:text-gray-100 focus:border-indigo-500 focus:ring focus:ring-blue-100/50 w-64" />
                </div>
            </div>
        </div>

        <div class="overflow-hidden rounded-md border border-light-border dark:border-dark-border bg-primary-bg dark:bg-primary-dark-bg pb-3 shadow-md">
            <div class="w-full overflow-x-auto">
                <table class="min-w-full text-left text-sm text-secondary-text dark:text-gray-300">
                    <thead class="bg-gray-100 dark:bg-gray-800 font-medium">
                        <tr>
                            <th class="px-3 md:px-6 py-2 md:py-4">Sürücü</th>
                            <th class="px-3 md:px-6 py-2 md:py-4">TC No</th>
                            <th class="px-3 md:px-6 py-2 md:py-4">Ehliyet No</th>
                            <th class="px-3 md:px-6 py-2 md:py-4">Toplam Borç</th>
                            <th class="px-3 md:px-6 py-2 md:py-4">İşlemler</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="loans.length === 0">
                            <td colspan="5" class="text-center py-6 text-gray-500 dark:text-gray-400">
                                Veresiye borcu bulunamadı.
                            </td>
                        </tr>

                        <tr v-for="book in loans" :key="book.book_id"
                            class="border-b transition hover:light-hover dark:hover:dark-hover">
                            <td class="px-3 md:px-6 py-2 md:py-4 text-blue-600 hover:underline  cursor-pointer"
                                @click="$router.push(`/drivers/${book.driver.driver_id}/books`)">
                                {{ book.driver?.driver_name || '-' }}
                            </td>
                            <td class="px-3 md:px-6 py-2 md:py-4 text-gray-600">
                                {{ book.driver?.driver_tcno || '-' }}
                            </td>
                            <td class="px-3 md:px-6 py-2 md:py-4 text-gray-600">
                                {{ book.driver?.driver_licenceno || '-' }}
                            </td>
                            <td class="px-3 md:px-6 py-2 md:py-4" :class="{
                                'text-red-400': book.total_amount > 0
                            }">
                                {{ getFormattedAmount(book.total_amount) }}
                            </td>
                            <td class="px-3 md:px-6 py-2 md:py-4">
                                <button v-if="book.total_amount > 0" @click="openModal(book)"
                                    class="bg-primary-button hover:bg-primary-button-hover text-white px-4 py-2 rounded transition-all duration-200 text-sm">
                                    Ödeme Yap
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <AppPagination 
              :currentPage="currentPage" 
              :totalPages="totalPages" 
              :pageSize="pageSize"
              :totalItems="totalLoansCount"
              @prev-page="currentPage--"
              @next-page="currentPage++" 
              @go-to-page="(page) => (currentPage = page)"
              @update:pageSize="handlePageSizeChange"
            />
        </div>
    </div>

    <div v-if="showModal" class="fixed inset-0 z-50 p-4 flex items-center justify-center bg-black bg-opacity-50"
        @click.self="closeModal">
        <div class="bg-white dark:bg-gray-900 p-6 rounded-lg w-full max-w-md shadow-lg relative">

            <button @click="closeModal"
                class="absolute top-2 right-2 p-1 text-gray-400 hover:text-secondary-text dark:text-gray-300 dark:hover:text-white">
                <XMarkIcon class="w-6 h-6" />
            </button>

            <div class="mb-4">
                <label class="block text-sm font-medium mb-1 primary-text dark:text-gray-300">Ödeme Miktarı (₺)</label>
                <input v-model.number="paymentAmount" type="number"
                    :max="selectedLoan.amount - selectedLoan.paid_amount"
                    class="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:text-white" />
            </div>

            <div class="mb-4">
                <label class="block text-sm font-medium mb-1 primary-text dark:text-gray-300">Ödeme Türü</label>
                <select v-model="paymentType" class="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:text-white">
                    <option value="cash">Nakit</option>
                    <option value="creditCard">Kredi Kartı</option>
                </select>
            </div>

            <div class="mb-4">
                <label class="block text-sm font-medium mb-1 primary-text dark:text-gray-300">Açıklama</label>
                <input v-model="paymentDescription" type="text"
                    class="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:text-white" 
                    placeholder="Ödeme açıklaması" />
            </div>

            <div class="flex justify-end gap-3 mt-6">
                <button @click="closeModal" class="px-4 py-2 rounded border text-sm primary-text dark:text-gray-300">
                    Vazgeç
                </button>
                <button @click="submitPayment"
                    class="bg-primary-button hover:bg-primary-button-hover text-white px-4 py-2 rounded text-sm">
                    Kaydet
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { getApi, postApi } from "@/services/api";
import { XMarkIcon } from "@heroicons/vue/24/outline";
import AppPagination from "@/components/AppPagination.vue";
import { DateTime } from "luxon";

export default {
    name: "LoanPage",
    components: {
        XMarkIcon,
        AppPagination,
    },
    data() {
        return {
            loans: [],
            isLoading: false,
            showModal: false,
            selectedLoan: null,
            paymentAmount: 0,
            paymentType: 'cash',
            paymentDescription: '',
            filterType: "open",
            totalLoansCount: 0,
            currentPage: 1,
            pageSize: 50,
            searchText: "",
            driverSearchResults: [],
            selectedDriver: null,
            focusedIndex: -1,
            startDate: "",
            endDate: "",
            totalDebt: 0,
            veresiyeSearchText: '',
            searchTimeout: null,
        };
    },
    computed: {
        remainingAmount() {
            if (!this.selectedLoan) return 0;
            return this.selectedLoan.amount - this.selectedLoan.paid_amount;
        },

        totalPages() {
            return Math.ceil(this.totalLoansCount / this.pageSize);
        },
    },
    methods: {
        formatPrice(value) {
            if (value === null || value === undefined) return '0,00';
            const numValue = parseFloat(value);
            if (isNaN(numValue)) return '0,00';
            const parts = numValue.toFixed(2).split('.');
            const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
            const decimalPart = parts[1];
            return `${integerPart},${decimalPart}`;
        },

        formatDate(dateString) {
            if (!dateString) return "-";
            return DateTime.fromISO(dateString).toFormat('dd.MM.yyyy HH:mm');
        },

        getFormattedAmount(bookAmount) {
            const numericAmount = parseFloat(bookAmount);

            if (isNaN(numericAmount)) {
                return "-";
            }

            return numericAmount + " ₺";
        },

        async onInput() {
            if (!this.searchText || this.searchText.length < 2) {
                this.driverSearchResults = [];
                return;
            }

            this.isLoading = true;
            try {
                const response = await getApi(`/drivers/query/${this.searchText}`);
                this.driverSearchResults = Array.isArray(response) ? response : [];
                this.focusedIndex = 0;
            } catch (err) {
                this.driverSearchResults = [];
            } finally {
                this.isLoading = false;
            }
        },

        moveFocus(direction) {
            if (!this.driverSearchResults.length) return;

            const total = this.driverSearchResults.length;
            this.focusedIndex = (this.focusedIndex + direction + total) % total;
        },

        selectFocused() {
            if (
                this.focusedIndex >= 0 &&
                this.focusedIndex < this.driverSearchResults.length
            ) {
                const driver = this.driverSearchResults[this.focusedIndex];
                this.selectDriver(driver);
            }
        },
        changeFilter(type) {
            this.filterType = type;
            this.currentPage = 1;
            this.fetchBooks();
        },

        clearDateFilter() {
            this.startDate = "";
            this.endDate = "";
            this.currentPage = 1;
            this.fetchBooks();
        },

        openModal(book) {
            this.selectedLoan = {
                loan_id: book.book_id,
                driver: book.driver,
                amount: book.total_amount,
                paid_amount: this.getPaidAmount(book.book_id),
            };
            this.paymentAmount = 0;
            this.paymentType = 'cash';
            this.paymentDescription = '';
            this.showModal = true;
        },

        closeModal() {
            this.showModal = false;
            this.selectedLoan = null;
            this.paymentAmount = 0;
            this.paymentType = 'cash';
            this.paymentDescription = '';
        },

        async submitPayment() {
            const value = this.paymentAmount;
            const remaining = this.remainingAmount;

            if (!value || value <= 0) {
                this.$toast.error("Geçerli bir ödeme miktarı giriniz.");
                return;
            }

            if (Number(value.toFixed(2)) > Number(remaining.toFixed(2))) {
                this.$toast.error("Ödeme miktarı kalan borçtan fazla olamaz.");
                return;
            }

            try {
                const bookletPayload = {
                    book_id: this.selectedLoan.loan_id,
                    driver_id: this.selectedLoan.driver.driver_id,
                    amount: this.paymentAmount,
                    is_payment: true,
                    description: this.paymentDescription || null,
                };

                await postApi("/books/booklet", bookletPayload);

                const description = this.paymentDescription 
                    ? `VERESİYE - ${this.selectedLoan.driver.driver_name} - ${this.selectedLoan.driver.driver_tcno} - ${this.paymentDescription}`
                    : `VERESİYE - ${this.selectedLoan.driver.driver_name} - ${this.selectedLoan.driver.driver_tcno}`;

                const cashflowPayload = {
                    type: 'in',
                    amount: this.paymentAmount,
                    description: description,
                    driver_id: this.selectedLoan.driver.driver_id,
                    vehicle_plate: this.selectedLoan.driver.driver_licenceno,
                };

                if (this.paymentType === 'creditCard') {
                    await postApi("/cashflow/credit-card", cashflowPayload);
                } else {
                    await postApi("/cashflow/", cashflowPayload);
                }

                this.$toast.success("Ödeme başarıyla kaydedildi.");
                this.closeModal();
                this.fetchBooks();
            } catch (err) {
                this.$toast.error("Ödeme kaydedilirken bir hata oluştu.");
            }
        },

        async getPaidAmount(bookId) {
            try {
                const take = this.pageSize;
                const skip = (this.currentPage - 1) * this.pageSize;

                const driverId = this.loans.find((l) => l.book_id === bookId)?.driver
                    ?.driver_id;
                if (!driverId) return 0;

                const response = await getApi(
                    `/books/driver/${driverId}/${take}/${skip}`
                );

                const payments =
                    response.booklets?.filter(
                        (b) => b.book_id === bookId && b.is_payment
                    ) || [];
                return payments.reduce((sum, p) => sum + p.amount, 0);
            } catch (err) {
                return 0;
            }
        },

        async searchDrivers() {
            if (!this.searchText || this.searchText.length < 2) {
                this.driverSearchResults = [];
                return;
            }

            try {
                const response = await getApi(`/drivers/query/${this.searchText}`);
                this.driverSearchResults = Array.isArray(response) ? response : [];
            } catch (error) {
                this.driverSearchResults = [];
            }
        },

        async fetchDrivers() {
            const q = this.searchQuery.trim();

            if (q.length >= 3) {
                const response = await getApi(`/drivers/query/${encodeURIComponent(q)}`);

                this.drivers = Array.isArray(response) ? response : [];
                this.totalDriversCount = this.drivers.length;
            }
            else {
                const take = this.pageSize;
                const skip = (this.currentPage - 1) * this.pageSize;
                const response = await getApi(`/drivers/${take}/${skip}`);
                this.drivers = response.drivers;
                this.totalDriversCount = response.total_result;
            }
        },

        handlePageSizeChange(newPageSize) {
          this.pageSize = newPageSize;
          this.currentPage = 1;
          this.fetchBooks();
        },

        async fetchBooks() {
            this.isLoading = true;
            try {
                const take = this.pageSize;
                const skip = (this.currentPage - 1) * this.pageSize;

                const search = this.veresiyeSearchText ? this.veresiyeSearchText.trim() : "";

                let url = `/books/all/${take}/${skip}`;
                if (search) {
                    url += `?search=${encodeURIComponent(search)}`;
                }

                const response = await getApi(url);

                this.loans = Array.isArray(response) ? response : response.books || [];
                this.totalLoansCount = response.total_result || this.loans.length;

                await this.calculateSummary();

            } catch (error) {
                this.loans = [];
                this.totalLoansCount = 0;
            } finally {
                this.isLoading = false;
            }
        },

        async calculateSummary() {
            try {
                const response = await getApi('/books/total-loan');
                this.totalDebt = response.total_debt || 0;
                
            } catch (error) {
                console.error('Toplam borç hesaplama hatası:', error);
                this.totalDebt = 0;
            }
        },

        filterVeresiyeLoans() {
            clearTimeout(this.searchTimeout);
            this.searchTimeout = setTimeout(() => {
                this.currentPage = 1; 
                this.fetchBooks();
            }, 300);
        },

        applyFilter() {
            this.fetchBooks();
        },

        async getAllDeliveriesMap() {
            const take = this.pageSize;
            const skip = (this.currentPage - 1) * this.pageSize;

            let allApproved = [];
            let allUnapproved = [];

            try {
                const approvedRes = await getApi(`/deliveries/approved/${take}/${skip}`);
                allApproved = approvedRes?.deliveries || [];

                const unapprovedRes = await getApi(`/deliveries/unapproved/${take}/${skip}`);
                allUnapproved = unapprovedRes?.deliveries || [];

                const approvedMap = {};
                const pendingMap = {};

                allApproved.forEach(d => {
                    const orderId = d.order_id;
                    const tonnage = parseFloat(d.delivery_tonnage || 0);
                    if (!orderId) return;
                    approvedMap[orderId] = (approvedMap[orderId] || 0) + tonnage;
                });

                allUnapproved.forEach(d => {
                    const orderId = d.order_id;
                    const tonnage = parseFloat(d.delivery_tonnage || 0);
                    if (!orderId) return;
                    pendingMap[orderId] = (pendingMap[orderId] || 0) + tonnage;
                });

                return { approvedMap, pendingMap };
            } catch (error) {
                return { approvedMap: {}, pendingMap: {} };
            }
        },
    },
    async mounted() {
        await this.fetchBooks();
    },
    watch: {
        currentPage() {
            this.fetchBooks();
        },
        pageSize() {
            this.currentPage = 1;
            this.fetchBooks();
        },
        searchQuery: {
            handler(val) {
                this.currentPage = 1;
                if (val.trim().length === 0 || val.trim().length >= 3) {
                    this.fetchDrivers();
                }
            },
            immediate: false,
        },
    },
};
</script>
